/**
 * Bookmarks (Treasure Chest)
 * --------------------------
 * Persistent saved-item store backed by the per-user subcollection
 *   users/{uid}/bookmarks/{bookmarkId}
 *
 * Document shape:
 *   { refType: "business" | "event" | "property",
 *     refId:   string,
 *     snapshotTitle?: string,
 *     snapshotImage?: string,
 *     snapshotHref?:  string,
 *     savedAt:        Timestamp }
 *
 * Reads use one-shot getDocs (per project convention). On first read for a
 * given user, any legacy `favoriteListings[]` on the user doc is migrated
 * via the migrateLegacyFavorites callable.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { firestore, functions } from "../FirebaseConfig";
import { useAuth } from "../contexts/CombinedAuthContext";
import { usePlans } from "../contexts/PlansContext";
import { favoriteCapFor } from "../constants/plans";

const BookmarksContext = createContext(null);

export function BookmarksProvider({ children }) {
  const { user, isAuthenticated } = useAuth() || {};
  const plans = usePlans();
  const getActiveTravelerTier = plans?.getActiveTravelerTier;
  const uid = isAuthenticated ? user?.uid || user?.member?.uid : null;
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading]   = useState(false);
  const migratedFor = useRef(new Set());

  const reload = useCallback(async () => {
    if (!uid) {
      setBookmarks([]);
      return;
    }
    setLoading(true);
    try {
      // Lazy migration: one-time per session, before reading.
      if (!migratedFor.current.has(uid)) {
        try {
          const migrate = httpsCallable(functions, "migrateLegacyFavorites");
          await migrate({});
        } catch (err) {
          // Non-fatal: user may not have legacy data, or callable may
          // briefly be unavailable. We still load the subcollection.
          console.warn("migrateLegacyFavorites failed (non-fatal):", err);
        }
        migratedFor.current.add(uid);
      }
      const ref = collection(firestore, "users", uid, "bookmarks");
      const snap = await getDocs(ref);
      const out = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      out.sort((a, b) => {
        const ta = a.savedAt?.toMillis?.() || 0;
        const tb = b.savedAt?.toMillis?.() || 0;
        return tb - ta;
      });
      setBookmarks(out);
    } catch (err) {
      console.error("loadBookmarks failed:", err);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => { reload(); }, [reload]);

  const isBookmarked = useCallback(
    (refType, refId) => bookmarks.some((b) => b.refType === refType && b.refId === refId),
    [bookmarks]
  );

  const findBookmark = useCallback(
    (refType, refId) => bookmarks.find((b) => b.refType === refType && b.refId === refId) || null,
    [bookmarks]
  );

  const addBookmark = useCallback(async ({ type, id, snapshot = {} }) => {
    if (!uid) throw new Error("Sign in to save treasure.");
    if (!type || !id) throw new Error("Bookmark requires type and id.");
    if (isBookmarked(type, id)) return;

    // Enforce the tier favorite cap. Falls back to Deckhand (10) when no
    // active tier is resolved (e.g. PlansProvider not mounted yet).
    const activeTier = typeof getActiveTravelerTier === "function"
      ? getActiveTravelerTier()
      : null;
    const cap = favoriteCapFor(activeTier);
    if (bookmarks.length >= cap) {
      const err = new Error(
        `You've reached your favorite limit (${cap}). Upgrade your pass to save more.`
      );
      err.code = "favorite-cap-reached";
      throw err;
    }

    const payload = {
      refType: type,
      refId: id,
      snapshotTitle: snapshot.title || null,
      snapshotImage: snapshot.image || null,
      snapshotHref: snapshot.href || null,
      savedAt: serverTimestamp(),
    };
    // Optimistic
    const optimistic = { id: `optimistic-${type}-${id}`, ...payload, savedAt: { toMillis: () => Date.now() } };
    setBookmarks((prev) => [optimistic, ...prev]);
    try {
      const ref = await addDoc(collection(firestore, "users", uid, "bookmarks"), payload);
      setBookmarks((prev) => prev.map((b) => (b.id === optimistic.id ? { ...optimistic, id: ref.id } : b)));
    } catch (err) {
      setBookmarks((prev) => prev.filter((b) => b.id !== optimistic.id));
      throw err;
    }
  }, [uid, isBookmarked, bookmarks.length, getActiveTravelerTier]);

  const removeBookmark = useCallback(async ({ type, id, bookmarkId }) => {
    if (!uid) return;
    const target = bookmarkId
      ? bookmarks.find((b) => b.id === bookmarkId)
      : findBookmark(type, id);
    if (!target) return;
    // Optimistic
    setBookmarks((prev) => prev.filter((b) => b.id !== target.id));
    try {
      await deleteDoc(doc(firestore, "users", uid, "bookmarks", target.id));
    } catch (err) {
      // Restore on failure
      setBookmarks((prev) => [target, ...prev]);
      throw err;
    }
  }, [uid, bookmarks, findBookmark]);

  const value = useMemo(() => ({
    bookmarks,
    loading,
    isBookmarked,
    addBookmark,
    removeBookmark,
    reload,
    count: bookmarks.length,
  }), [bookmarks, loading, isBookmarked, addBookmark, removeBookmark, reload]);

  return (
    <BookmarksContext.Provider value={value}>
      {children}
    </BookmarksContext.Provider>
  );
}

export function useBookmarks() {
  const ctx = useContext(BookmarksContext);
  if (!ctx) {
    return {
      bookmarks: [],
      loading: false,
      isBookmarked: () => false,
      addBookmark: async () => {},
      removeBookmark: async () => {},
      reload: async () => {},
      count: 0,
    };
  }
  return ctx;
}

// Back-compat aliases so legacy imports continue to function.
export const FavoritesProvider = BookmarksProvider;
export const useFavorites = useBookmarks;
